import { trechoDepartureDate, trechoTimeAtKm } from "@/lib/segment-times"

/**
 * Regras de tempo/visibilidade do resumo da viagem (`/trip-summary`).
 *
 * Estas funções são a FONTE ÚNICA das regras: o front (`TripSummaryContent`)
 * e a API (`/api/rides/[id]`, `/api/rides/[id]/reservations/[reservationId]`)
 * usam exatamente o mesmo cálculo. Divergir entre as duas pontas é o que
 * permite o usuário ver um botão que a API recusa (ou vice-versa).
 *
 * São funções PURAS (sem `mongodb`, sem `next/server`) para poderem ser
 * importadas por client components.
 */

/** Tolerância (minutos) entre o horário de embarque e a liberação da denúncia. */
export const NO_SHOW_GRACE_MINUTES = 15

/** Campos da reserva usados pelas regras de tempo. */
export interface TimedReservation {
  status?: string
  /** Horário de embarque CONGELADO no momento da confirmação (fonte do repasse). */
  boardEstimatedAt?: string | Date | null
  boardStopIndex?: number | null
  alightStopIndex?: number | null
  boardKm?: number | null
  alightKm?: number | null
}

/** Campos da carona usados pelas regras de tempo. */
export interface TimedRide {
  status?: string
  settledAt?: string | Date | null
  turboMode?: boolean
  date?: string
  departureTime?: string
}

/**
 * Documento "cru" (Mongo/JSON) com os campos das regras.
 *
 * O índice permite que o documento do Mongo (que tipa tudo como `any`) seja
 * aceito sem cast, sem afrouxar a tipagem dos campos lidos.
 */
export type RideLike = TimedRide & Record<string, unknown>
export type ReservationLike = TimedReservation & Record<string, unknown>

/** O que as regras aceitam como "carona". */
export type RideInput = TimedRide | RideLike
/** O que as regras aceitam como "reserva". */
export type ReservationInput = TimedReservation | ReservationLike

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null
  const d = value instanceof Date ? value : new Date(value)
  return isNaN(d.getTime()) ? null : d
}

/**
 * Horário de embarque do passageiro no seu TRECHO.
 *
 * Ordem de precedência:
 * 1. `boardEstimatedAt` — congelado quando o motorista confirmou a reserva.
 *    É a fonte que o cron de repasse usa, então é a que manda.
 * 2. Fallback calculado a partir da carona: Turbo usa o KM do embarque
 *    (`trechoTimeAtKm`), demais usam o índice da parada (`trechoDepartureDate`).
 *    Cobre reservas confirmadas sem o campo congelado (dado legado) e
 *    reservas pendentes.
 *
 * Devolve `null` quando não há como saber (sem data/horário na carona) —
 * nesse caso NENHUMA regra de tempo pode ser aplicada (o passageiro continua
 * cancelável e a denúncia não abre).
 */
export function boardingTimeFor(
  ride: RideInput | null | undefined,
  reservation: ReservationInput | null | undefined
): Date | null {
  if (!ride || !reservation) return null

  const frozen = toDate(reservation.boardEstimatedAt)
  if (frozen) return frozen

  if (ride.turboMode && typeof reservation.boardKm === "number") {
    return trechoTimeAtKm(ride, reservation.boardKm)
  }

  if (typeof reservation.boardStopIndex === "number") {
    return trechoDepartureDate(ride, reservation.boardStopIndex)
  }

  return null
}

/**
 * A reserva ainda pode ser cancelada? Só antes do horário de embarque do
 * TRECHO do passageiro — depois do embarque a reserva virou viagem e não pode
 * mais ser desfeita (vale igual para passageiro e motorista).
 *
 * Sem horário de embarque conhecido ⇒ `true` (não bloquear por falta de dado).
 */
export function canCancelReservation(
  ride: RideInput | null | undefined,
  reservation: ReservationInput | null | undefined,
  now: Date = new Date()
): boolean {
  const boarding = boardingTimeFor(ride, reservation)
  if (!boarding) return true
  return boarding.getTime() > now.getTime()
}

/**
 * Já passou o prazo de tolerância para denunciar "não compareceu"?
 *
 * O prazo abre 15 minutos DEPOIS do horário de embarque: antes disso a
 * pessoa ainda pode estar a caminho (trânsito, atraso do motorista, ponto
 * longe). Depois disso, o não comparecimento é reportável.
 */
export function canReportNoShow(
  ride: RideInput | null | undefined,
  reservation: ReservationInput | null | undefined,
  now: Date = new Date()
): boolean {
  const boarding = boardingTimeFor(ride, reservation)
  if (!boarding) return false
  const deadline = boarding.getTime() + NO_SHOW_GRACE_MINUTES * 60 * 1000
  return now.getTime() >= deadline
}

/**
 * A carona foi LIQUIDADA? `settledAt` é o que o cron grava ao liquidar a
 * carona (é a mesma definição usada por `trip-stats` e pelo badge de
 * cancelamento). Uma carona liquidada já aconteceu — não há mais o que
 * editar ou cancelar.
 */
export function isRideSettled(ride: RideInput | null | undefined): boolean {
  if (!ride) return false
  return !!ride.settledAt
}

/**
 * A viagem acabou? Carona liquidada (`settledAt`) ou cancelada.
 */
export function isRideFinished(ride: RideInput | null | undefined): boolean {
  if (!ride) return false
  if (isRideSettled(ride)) return true
  return ride.status === "cancelled"
}

/**
 * Por que a carona NÃO pode mais ser editada? `null` = pode editar.
 *
 * FONTE ÚNICA do gate e da MENSAGEM: a página `/manage-ride/[id]` e a API
 * `PUT /api/rides/[id]` precisam recusar pelo mesmo motivo e com a mesma
 * frase — senão o motorista edita no formulário e só descobre no "salvar"
 * que a carona não é mais editável (e vice-versa).
 *
 * Ordem: liquidada (a viagem aconteceu) → cancelada → qualquer outro status
 * fora de "active" (ex.: `pending_payment`, carona ainda não publicada).
 */
export function notEditableReason(ride: RideInput | null | undefined): string | null {
  if (!ride) return null
  if (isRideSettled(ride)) return "Esta carona já foi concluída e não pode mais ser editada."
  if (ride.status === "cancelled") return "Esta carona foi cancelada e não pode mais ser editada."
  if (ride.status !== "active") return "Esta carona não pode mais ser editada."
  return null
}

/**
 * A carona ainda pode ser GERENCIADA (editada)? Só carona ativa e não
 * liquidada. O mesmo gate da página `/manage-ride/[id]`.
 */
export function isRideManageable(ride: RideInput | null | undefined): boolean {
  if (!ride) return false
  return notEditableReason(ride) === null
}
